import { programme } from "../funnel/conferences";
import { useConferences } from "../funnel/useConferences";
import { fallbackOf } from "../settings/catalog";
import { useLocale, useT, useText } from "../settings/context";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * ═══ LE PROGRAMME — TOUT CE QUI SE PASSE, ET CE QU'ON A RETENU ═══
 *
 * Une page du site, pas une étape : on la consulte pendant l'événement, le téléphone à
 * la main, pour savoir ce qui se passe et à quelle heure.
 *
 * ⚠️ UNE SEULE LISTE. Elle a porté deux onglets — tout, ou seulement le mien — retirés
 * le 2026-08-05 : ce qu'ils apportaient est désormais dans la liste elle-même, où une
 * séance retenue prend la couleur de marque. Un mode à comprendre en moins, un écran
 * vide en moins pour qui n'a rien choisi, et le programme reste ce qu'on vient y
 * chercher.
 *
 * ⚠️ ELLE NE RÉSERVE RIEN, ET C'EST TOUTE LA DIFFÉRENCE AVEC L'ÉTAPE. L'étape
 * (`/registration/conferences`) est un formulaire : on y clique pour prendre ou rendre
 * une place, avec les verrous du serveur et une question avant de défaire. Ici on LIT.
 * Des cartes cliquables auraient ouvert un second chemin d'inscription — sans les
 * verrous, puisque la page ne les affiche pas — et deux façons de s'inscrire finissent
 * par se contredire. La maquette dit la même chose : elle pose des `.card`, pas des
 * `.conf`.
 *
 * ⚠️ LA MÊME LECTURE QUE L'ÉTAPE, sous la même clé de requête (`useConferences`) :
 * passer du tunnel au programme ne redemande pas ce qui vient d'être lu, et
 * l'inscription prise à l'étape se voit ici à l'instant.
 *
 * ⚠️ LA V1 N'A PAS CET ÉCRAN — elle n'a que l'étape (`screens/Conferences/`). Aucune
 * correspondance à déclarer, donc, et rien à reprendre : c'est une page que la V2
 * ajoute. Ses réglages n'ont pas d'`origin`, et c'est exact plutôt qu'oublié.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */
export function Programme() {
  const t = useT();
  const locale = useLocale();

  const title = useText(
    "FO_MEETING_V2_PROG_TITLE",
    fallbackOf("FO_MEETING_V2_PROG_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_PROG_SUBTITLE",
    fallbackOf("FO_MEETING_V2_PROG_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_PROG_HTML");

  const { conferences, loading, failure } = useConferences(true);

  const days = programme(conferences, locale);
  const empty = !loading && !failure && days.length === 0;

  return (
    <section className="page">
      <div className="ttl" data-cfg="progTitle">
        <InPlace settingKey="FO_MEETING_V2_PROG_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_PROG_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="progTitle" />
      )}

      {loading && (
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_PROGRAMME", 'Chargement du programme')}
          </span>
        </p>
      )}

      {/* ⚠️ UNE PANNE SE DIT. Rendue en « aucune conférence », elle ferait croire à un
          événement sans programme — et le participant n'essaierait pas de revenir. */}
      {failure && (
        <p className="loginerr" role="alert">
          {t("PROGRAMME_NOT_LOADED", "Le programme n'a pas pu être chargé. Réessayez dans un instant.")}
        </p>
      )}

      <div className="clist" data-cfg="progTitle">
        {days.map((day) => (
          <div className="cvday" key={day.key}>
            <div className="cvday__h">
              <b>{day.day}</b>
            </div>
            {/* ⚠️ UNE SÉANCE RETENUE EST MARQUÉE, PAS SEULEMENT ANNOTÉE. Dans quarante
                séances, une ligne « Vous êtes inscrit » en petit sous un titre ne se
                repère pas : on parcourt la page à la recherche de SES créneaux, et c'est
                la carte entière qui doit répondre. `.card--on` est le mot de la maquette
                pour une carte retenue. */}
            {day.sessions.map((session) => (
              <div
                className={`card${session.slot.selected ? " card--on" : ""}`}
                key={session.slot.id}
              >
                <b>
                  {session.hours} — {session.conference.title}
                </b>
                {/* ⚠️ « VOUS ÊTES INSCRIT » D'ABORD, et la présentation ensuite : c'est
                    ce qui distingue MES séances des autres, et reléguée après un
                    paragraphe de description elle ne se voit plus. La maquette la met au
                    même endroit, à la suite de la salle — que l'API ne porte pas. */}
                {(session.slot.selected || session.detail) && (
                  <small>
                    {[
                      session.slot.selected
                        ? t("YOU_ARE_REGISTERED", "Vous êtes inscrit")
                        : null,
                      session.detail || null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ⚠️ LE VIDE SE DIT. Une page qui ne montre rien se lit comme une panne, et
          personne ne revient sur un écran qu'il croit cassé. */}
      {empty && (
        <p className="empty">
          {t("PROGRAMME_NOT_PUBLISHED", "Le programme n'est pas encore publié. Revenez d'ici quelques jours.")}
        </p>
      )}
    </section>
  );
}
